import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import GoogleSignInButton from './GoogleSignInButton';
import pendingActionService from '../services/pendingActionService';
import { trackLandingView, trackCTAClick, trackSignupStart, trackSignupComplete, trackLogin } from '../utils/analytics';
import { getPostAuthRedirect } from '../utils/authRedirect';
import SEO from './SEO';
import PersonaCrossSell from './PersonaCrossSell';
import { PAGE_SEO, buildBreadcrumbs } from '../utils/seoConfig';
import './PersonaLanding.css';
import './BusinessLanding.css';

const BusinessLanding = () => {
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(null);

  useEffect(() => { trackLandingView('business'); }, []);

  const handleGoogleSuccess = async (credentialResponse) => {
    trackSignupStart('business');
    try {
      if (!credentialResponse.credential) { setAuthError('Missing authentication credentials'); return; }
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ credential: credentialResponse.credential }),
      });
      if (!response.ok) throw new Error(`Authentication failed: ${response.status}`);
      const data = await response.json();
      const userData = { ...data.user, sub: data.user.sub || data.user.id || data.user.email };
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('token', data.token);
      if (data.isNewUser) {
        trackSignupComplete('business', userData.sub || userData.email);
      } else {
        trackLogin('business', userData.sub || userData.email);
      }
      const redirectUrl = await pendingActionService.checkAndExecutePendingAction();
      const next = getPostAuthRedirect({
        isNewUser: data.isNewUser,
        pendingRedirectUrl: redirectUrl,
        pendingSubscription: localStorage.getItem('pendingSubscription'),
        defaultHref: null
      });
      if (next) {
        if (redirectUrl) toast.success('Welcome!');
        window.location.href = next;
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Auth error:', error);
      setAuthError(`Login failed: ${error.message}`);
    }
  };

  const checkSvg = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>
  );

  const seo = PAGE_SEO['/for-business'];

  return (
    <div className="persona-landing for-business">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonical="/for-business"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'FreshFitLab Business',
            provider: {
              '@type': 'Organization',
              name: 'FreshFitLab',
              url: 'https://freshfitlab.io',
            },
            serviceType: 'Practice management software for PT clinics, gyms, and nutrition practices',
            description: seo.description,
            audience: {
              '@type': 'BusinessAudience',
              audienceType: 'Physical therapy clinics, gyms, nutrition practices, wellness centers',
            },
          },
          buildBreadcrumbs([
            { name: 'Home', url: '/' },
            { name: 'For Business', url: '/for-business' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What does FreshFitLab Business include?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Employee and client roles, QR-code/invite-link onboarding, exercise and meal plan assignment, progress tracking, SSO for enterprise teams, and tiered pricing.',
                },
              },
              {
                '@type': 'Question',
                name: 'Who is FreshFitLab Business for?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Physical therapy clinics, gyms, nutrition practices, and wellness centers that want a single platform to assign exercises and meal plans to their clients.',
                },
              },
            ],
          },
        ]}
      />
      <div className="pl-bg-shapes">
        <div className="pl-shape pl-shape-1"></div>
        <div className="pl-shape pl-shape-2"></div>
        <div className="pl-shape pl-shape-3"></div>
      </div>

      {/* ─── Hero ─── */}
      <section className="pl-hero">
        <span className="pl-hero-badge">For Physical Therapy &amp; Nutrition Clinics</span>
        <h1>
          Manage Your Practice,<br />
          <span className="pl-highlight">All in One Place</span>
        </h1>
        <p className="pl-hero-subtitle">
          Onboard your team, assign exercises and nutrition plans to clients, and track outcomes —
          with enterprise-grade tools built for health &amp; wellness businesses.
        </p>

        <div className="pl-hero-ctas">
          <button className="pl-cta-primary" onClick={() => { trackCTAClick('business', 'hero'); navigate('/pricing#pricing-business'); }}>
            <span>View Business Plans</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('business', 'hero_secondary'); navigate('/business/register'); }}>
            <span>Register Your Business</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>

        <div className="pl-login-divider"><span>or</span></div>
        <div className="pl-login-section">
          <p className="pl-login-label">Already registered? Sign in</p>
          {process.env.NODE_ENV === 'development' ? (
            <button onClick={() => navigate('/dev/login')} className="pl-dev-login-button">Dev Login</button>
          ) : (
            <GoogleSignInButton onSuccess={handleGoogleSuccess} onError={() => setAuthError('Login Failed')} text="signin" shape="pill" />
          )}
          {authError && <p className="pl-auth-error">{authError}</p>}
        </div>
      </section>

      {/* ─── How It Works ─── */}
      <section className="pl-steps">
        <h2 className="pl-steps-title">How It Works</h2>
        <div className="pl-steps-grid">
          <div className="pl-step">
            <div className="pl-step-number">1</div>
            <h3>Onboard Your Team</h3>
            <p>Register your business, invite employees, and set roles — owner, therapist, or nutritionist.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">2</div>
            <h3>Assign Plans to Clients</h3>
            <p>Create exercise programs and nutrition plans, then assign them to individual clients.</p>
          </div>
          <div className="pl-step">
            <div className="pl-step-number">3</div>
            <h3>Track Outcomes</h3>
            <p>Monitor client adherence, review progress, and adjust plans — all from one dashboard.</p>
          </div>
        </div>
      </section>

      {/* ─── Features ─── */}
      <section className="pl-features">
        <h2 className="pl-features-title">Built for Health &amp; Wellness Businesses</h2>
        <div className="pl-features-grid">
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-roles"><i className="fas fa-user-shield"></i></div>
            <h3>Multi-Role Access</h3>
            <p>Business owner and employee roles with scoped permissions. Everyone sees only what they need.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-qr"><i className="fas fa-qrcode"></i></div>
            <h3>QR Code Onboarding</h3>
            <p>Clients join your practice by scanning a QR code, clicking a link, or accepting an invite.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-assign"><i className="fas fa-clipboard-list"></i></div>
            <h3>Exercise &amp; Nutrition Assignments</h3>
            <p>Assign tailored exercise programs and meal plans to each client from your library.</p>
          </div>
          <div className="pl-feature-card">
            <div className="pl-feature-icon icon-sso"><i className="fas fa-building"></i></div>
            <h3>Enterprise SSO</h3>
            <p>Single sign-on integration for large teams. Tiered plans from Starter to Enterprise.</p>
          </div>
        </div>
      </section>

      {/* ─── Dashboard Visual ─── */}
      <section className="pl-split">
        <div className="pl-split-text">
          <h2>Your Practice at a Glance</h2>
          <p>
            A centralised dashboard lets you monitor employees, clients, and engagement — so you can
            focus on care, not administration.
          </p>
          <ul className="pl-perks">
            <li>{checkSvg}<span>Business owner &amp; employee roles</span></li>
            <li>{checkSvg}<span>Client onboarding via QR code, link, or invite</span></li>
            <li>{checkSvg}<span>Assign exercises &amp; meal plans to clients</span></li>
            <li>{checkSvg}<span>SSO integration for enterprise teams</span></li>
            <li>{checkSvg}<span>Tiered plans: Starter, Professional, Enterprise</span></li>
          </ul>
          <Link to="/pricing#pricing-business" className="pl-pricing-link">View Business Plans →</Link>
        </div>
        <div>
          <div className="pl-dashboard-card">
            <div className="pl-dash-header">
              <span className="pl-dash-title">Business Dashboard</span>
              <span className="pl-dash-badge">Pro Plan</span>
            </div>
            <div className="pl-dash-stats">
              <div><span className="pl-dash-stat-value">12</span><span className="pl-dash-stat-label">Employees</span></div>
              <div><span className="pl-dash-stat-value">48</span><span className="pl-dash-stat-label">Clients</span></div>
              <div><span className="pl-dash-stat-value">96%</span><span className="pl-dash-stat-label">Active</span></div>
            </div>
            <div className="pl-dash-features">
              <div className="pl-dash-row"><span className="pl-dash-dot"></span><span>QR code client onboarding</span></div>
              <div className="pl-dash-row"><span className="pl-dash-dot"></span><span>Exercise plan assignments</span></div>
              <div className="pl-dash-row"><span className="pl-dash-dot"></span><span>Nutrition plan assignments</span></div>
              <div className="pl-dash-row"><span className="pl-dash-dot"></span><span>Employee management</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA ─── */}
      <section className="pl-final-cta">
        <h2>Ready to modernise your practice?</h2>
        <p>Join clinics already managing their teams and clients on FreshFitLab.</p>
        <div className="pl-final-cta-buttons">
          <button className="pl-cta-primary pl-cta-dark" onClick={() => { trackCTAClick('business', 'footer'); navigate('/pricing#pricing-business'); }}>
            <span>View Business Plans</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
          <button className="pl-cta-secondary" onClick={() => { trackCTAClick('business', 'footer_secondary'); navigate('/business/register'); }} style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#fff', background: 'rgba(255,255,255,0.1)' }}>
            <span>Register Your Business</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </button>
        </div>
      </section>

      <PersonaCrossSell current="business" />
    </div>
  );
};

export default BusinessLanding;
